import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  CategoryInterface,
  MenuInterface,
  Modifier,
  ModifierGroupInterface,
  ModifierId,
  ProductInterface,
} from '../services/exports/Interfaces';
import collect, { Collection } from 'collect.js';
import MenuResource from '../services/resources/MenuResource';
import useImagePreloader from '../hooks/utility/useImagePreloader';
import {
  CategoryType,
  EntryType,
  ModifierGroupType,
  ORDER_METHODS,
  OfferKindEnum,
  REDUX_STORE_SLICES,
} from '../services/exports/Constants';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import useRewardAccount from '../hooks/loyalty/useRewardAccount';
import toFlatArray from '../utils/to-flat.util';
import { CompleteBasketItem } from './BasketProvider';
import useLoyaltyProgram from '../hooks/loyalty/useLoyaltyProgram';
import { selectWorkSchedule } from '../store/slices/InitialDataSlice';
import useScreenType from '../hooks/utility/useScreenType';
import useGetCompanyMenusApi from '../hooks/api/companies/useGetCompanyMenusApi';
import useBogoItemOffer from '../features/offers/hooks/useBogoItemOffer';

interface Props {
  children: ReactElement;
}

export interface MenuContextInterface {
  menus: Collection<MenuInterface>;
  selectedMenu: MenuInterface;
  productHeight: number;
  setSelectedMenu: (value: MenuInterface) => void;
  categories: CategoryInterface<WithActualPrice<ProductInterface>>[];
  allCategories: CategoryInterface<WithActualPrice<ProductInterface>>[];
  loadingMenu: boolean;
  compileProduct: (id: number) => CompiledProduct;
  findFreeProduct: (id: number) => CompiledProduct;
  applyOrderMethodPrices: <T extends ProductInterface | Modifier>(item: T) => WithActualPrice<T>;
  getUpsellModifierGroup: (
    basketItems: CompleteBasketItem[],
    openedProduct?: ProductInterface,
  ) => ModifierGroupInterface<CompiledProduct>;
  generateMenuScrollQuery: (categoryId: number, behavior?: ScrollBehavior) => string;
}

export type CompiledProduct = WithActualPrice<ProductInterface> & {
  modifier_groups: ModifierGroupInterface<CompiledModifier | CompiledProduct>[];
};

export type CompiledModifier = WithActualPrice<Modifier> & {
  modifier_groups: ModifierGroupInterface<CompiledModifier>[];
};

export type WithActualPrice<T> = T & {
  actual_price: number;
};

export const MenuContext = React.createContext<MenuContextInterface>(null);

export default function MenuProvider(props: Props) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Providers:MenuProvider' });

  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const { children } = props;
  const workSchedule = useSelector(selectWorkSchedule);
  const { data: menusData } = useGetCompanyMenusApi();

  const hasPreOrder = useSelector(
    (store: StoreInterface) => store[REDUX_STORE_SLICES.INITIAL_DATA].company?.has_pre_order,
  );
  const { preloadImages } = useImagePreloader();
  const { hasFreeItem } = useRewardAccount();
  const { isDesktop } = useScreenType();
  const { isActive: isLoyaltyProgramActive } = useLoyaltyProgram();

  const [selectedMenu, setSelectedMenu] = useState(null);

  const applyOrderMethodPrices = useCallback(
    <T extends ProductInterface | Modifier>(item: T): WithActualPrice<T> => {
      return {
        ...item,
        actual_price: item?.order_method_prices?.[cached_order?.method ?? ORDER_METHODS.PICKUP] ?? item?.base_price,
      };
    },
    [cached_order?.method],
  );
  const modifierGroupsState = useMemo(() => collect(menusData?.modifier_groups), [menusData?.modifier_groups]);

  const modifierGroups = useMemo(() => {
    return modifierGroupsState.map((item) => {
      const itemModifiers = item.modifier_ids?.map((modifier) => setupModifier(modifier)) ?? [];

      return {
        ...item,
        modifiers: itemModifiers,
      };
    });
  }, [modifierGroupsState, menusData?.modifiers, menusData?.products]);

  function setupModifier(modifier: ModifierId): CompiledProduct | CompiledModifier {
    const entry =
      modifier.type === EntryType.Product
        ? ({ ...menusData?.products[modifier.id] } as CompiledProduct)
        : ({ ...menusData?.modifiers[modifier.id] } as CompiledModifier);

    if (entry?.modifier_group_ids?.length && !entry.modifier_groups?.length) {
      entry.modifier_groups = entry.modifier_group_ids.map((modifierGroupId) => {
        const item = modifierGroupsState.get(modifierGroupId);
        const itemModifiers = item?.modifier_ids?.map((modifier) => setupModifier(modifier)) ?? [];

        return {
          ...item,
          modifiers: itemModifiers,
        } as ModifierGroupInterface<CompiledModifier>;
      });
    }

    return applyOrderMethodPrices(entry);
  }

  const compilePrice = <T extends CompiledProduct | CompiledModifier>(product: T): T => {
    return {
      ...product,
      actual_price: product.actual_price,
      modifier_groups: product.modifier_groups?.map((item) => ({
        ...item,
        modifiers: item.modifiers?.map((modifier) => ({
          ...modifier,
          actual_price: modifier.actual_price,
        })),
      })),
    };
  };

  const compileProduct = useCallback(
    (id: number): CompiledProduct => {
      const products = menusData?.products ?? {};
      const data = products[id] ?? null;

      if (!data) {
        return null;
      }

      return compilePrice(
        applyOrderMethodPrices({
          ...data,
          modifier_groups: collect(data?.modifier_group_ids)
            .map((id: number) => modifierGroups.get(id))
            .whereNotNull()
            .toArray(),
        } as CompiledProduct),
      );
    },
    [menusData?.products, modifierGroups, cached_order?.method],
  );

  const mappedCategories = useMemo<Collection<Collection<CategoryInterface<WithActualPrice<ProductInterface>>>>>(
    () =>
      collect(menusData?.menus).mapWithKeys((menu) => [
        menu.id,
        collect(menu.category_ids)
          .map((item: number) => {
            const category = menusData.categories[item];

            return category
              ? {
                  ...category,
                  menu_id: menu.id,
                  products:
                    category.product_ids?.map((id: number) => applyOrderMethodPrices(menusData?.products[id])) ?? [],
                }
              : null;
          })
          .whereNotNull()
          .filter((item) => item.products.length > 0),
      ]),
    [menusData?.menus, menusData?.categories, menusData?.products, cached_order?.method],
  );

  const upsellModifierGroup = useMemo(
    () =>
      modifierGroups.first<ModifierGroupInterface<CompiledProduct>>(
        (modifierGroup) => modifierGroup.type === ModifierGroupType.SystemUpsell,
      ),
    [modifierGroups],
  ) as ModifierGroupInterface<CompiledProduct>;

  const modifierGroupsToFlatModifiers = (
    modifierGroups: ModifierGroupInterface<CompiledModifier>[],
    result: (Modifier | ProductInterface)[] = [],
  ): (Modifier | ProductInterface)[] => {
    if (!modifierGroups?.length) {
      return result;
    }

    const nextData: ModifierGroupInterface<CompiledModifier>[] = [];

    modifierGroups.forEach((modifierGroup) => {
      if (!modifierGroup.modifier_ids?.length) {
        return;
      }

      result.push(...modifierGroup.modifiers);

      modifierGroup.modifiers.forEach((modifier) => {
        if (!modifier.modifier_group_ids?.length) {
          return;
        }

        nextData.push(...modifier.modifier_groups);
      });
    });

    return modifierGroupsToFlatModifiers(nextData, result);
  };

  const getUpsellModifierGroup = useCallback(
    (addedItems: CompleteBasketItem[], openedProduct?: CompiledProduct): ModifierGroupInterface<CompiledProduct> => {
      if (!upsellModifierGroup?.modifiers?.length) {
        return;
      }

      const flatAddedItems = addedItems?.length ? collect(toFlatArray(addedItems, 'modifiers')) : collect();

      const openedProducts = collect(
        modifierGroupsToFlatModifiers(openedProduct?.modifier_groups as ModifierGroupInterface<CompiledModifier>[]),
      ).where('type', EntryType.Product);
      const openedProductCategoryIds = openedProducts
        .pluck('category_ids')
        .collapse()
        .when(!!openedProduct?.category_ids?.length, (collection) => collection.concat(openedProduct?.category_ids));
      const openedProductNames = openedProducts
        .pluck('name')
        .when(!!openedProduct, (collection) => collection.push(openedProduct.name));

      const addedProducts = flatAddedItems.where('entry.type', EntryType.Product);
      const avoidedProductCategoryIds = addedProducts
        .pluck('entry.category_ids')
        .collapse()
        .concat(openedProductCategoryIds)
        .unique()
        .toArray();
      const avoidedProductNames = addedProducts.pluck('entry.name').concat(openedProductNames).unique().toArray();
      const productsByMenu = mappedCategories.get(selectedMenu?.id)?.pluck('product_ids').collapse().toArray() ?? [];

      const modifiers = collect(upsellModifierGroup.modifiers)
        .filter((modifier) => !avoidedProductNames.includes(modifier.name))
        .filter((modifier) => productsByMenu.includes(modifier.id))
        .filter(
          (modifier: ProductInterface) =>
            !modifier.category_ids.some((categoryId) => avoidedProductCategoryIds.includes(categoryId)),
        )
        .filter((modifier) => modifier.is_available)
        .unique('id')
        .toArray() as CompiledProduct[];

      if (modifiers.length > 2) {
        modifiers.length = 2;
      }

      return {
        ...upsellModifierGroup,
        modifiers,
      };
    },
    [modifierGroups, selectedMenu?.id, mappedCategories],
  );

  const menus = useMemo(() => {
    const menus = collect(menusData?.menus).filter(
      (item) =>
        (!hasPreOrder ? new MenuResource(item, workSchedule).isActiveToday() : true) &&
        mappedCategories.get(item?.id)?.isNotEmpty(),
    );

    return menus.isEmpty() ? menus.push(collect(menusData?.menus).first()) : menus;
  }, [menusData?.menus, hasPreOrder, workSchedule, mappedCategories]);

  const freeItems = useMemo<Collection<CompiledProduct>>(
    () =>
      collect(menusData?.products)
        .where('free_eligible', true)
        .map((item: ProductInterface) => {
          const data = compileProduct(item.id);
          const categories = collect(data.category_ids).filter((item) => {
            const category = menusData?.categories[item];

            if (!category) {
              return false;
            }

            return collect(menusData?.menus).whereIn('id', category.menu_ids).isNotEmpty();
          });

          return categories.isNotEmpty()
            ? {
                ...data,
                actual_price: 0,
                discount: 100,
                modifier_groups: collect(data.modifier_groups)
                  .whereNotNull()
                  .map((modifierGroup: ModifierGroupInterface) => ({
                    ...modifierGroup,
                    modifiers: collect(modifierGroup.modifiers)
                      .whereNotNull()
                      .map((modifier: CompiledModifier) => ({
                        ...modifier,
                        actual_price: modifier.free_eligible ? 0 : modifier.actual_price,
                      }))
                      .toArray(),
                  })),
              }
            : null;
        })
        .whereNotNull()
        .keyBy('id'),
    [menusData?.products, menusData?.categories, menusData?.menus, modifierGroups],
  );

  const bogoItemOffer = useBogoItemOffer();

  const bogoCategory: CategoryInterface<WithActualPrice<ProductInterface>> = useMemo(() => {
    if (!bogoItemOffer.offer || bogoItemOffer.offer.kind !== OfferKindEnum.Bogo) {
      return;
    }

    const bogoProducts = bogoItemOffer.offer.details.products
      .map((productId) => compileProduct(productId))
      ?.filter((product) => Boolean(product));

    if (!bogoProducts?.length) {
      return;
    }

    return {
      id: -1,
      menu_id: null,
      menu_ids: [],
      type: CategoryType.Bogo,
      name: t('labels.bogo'),
      image_url: null,
      product_ids: [],
      products: bogoProducts,
    };
  }, [bogoItemOffer.offer, menusData?.products]);

  const allCategories = useMemo<CategoryInterface<WithActualPrice<ProductInterface>>[]>(() => {
    const freeItemsCategory: CategoryInterface<WithActualPrice<ProductInterface>> = {
      id: 0,
      menu_id: null,
      menu_ids: [],
      type: CategoryType.FreeItems,
      name: t('labels.free_items'),
      image_url: null,
      product_ids: [],
      products: freeItems.toArray(),
    };

    return [
      ...(hasFreeItem && isLoyaltyProgramActive ? [freeItemsCategory] : []),
      ...(bogoCategory ? [bogoCategory] : []),
      ...((mappedCategories.get(selectedMenu?.id)?.toArray() as CategoryInterface<
        WithActualPrice<ProductInterface>
      >[]) ?? []),
    ];
  }, [hasFreeItem, isLoyaltyProgramActive, freeItems, selectedMenu, mappedCategories, i18n.language, bogoCategory]);

  const categories = useMemo<CategoryInterface<WithActualPrice<ProductInterface>>[]>(() => {
    const hasMostPopular = collect(allCategories).where('type', CategoryType.MostPopular).isNotEmpty();

    return collect(allCategories)
      .where('type', '!=', CategoryType.MostPopular)
      .when(hasMostPopular, (collection) => collection.where('type', '!=', CategoryType.Favourites))
      .toArray();
  }, [allCategories]);

  const findFreeProduct = useCallback<(id: number) => CompiledProduct>(
    (id: number): CompiledProduct => {
      return freeItems.get(id) ?? compileProduct(id);
    },
    [freeItems],
  );

  useEffect(() => {
    if (!selectedMenu) {
      setSelectedMenu(menus.firstWhere('is_active') ?? menus.first());
    }
  }, [menus]);

  useEffect(() => {
    if (!menus.firstWhere('id', selectedMenu?.id)) {
      setSelectedMenu(menus.firstWhere('is_active') ?? menus.first());
    }
  }, [menus]);

  useEffect(() => {
    if (selectedMenu) {
      preloadImages(
        collect(categories)
          .flatMap((category) => category?.products)
          .pluck('thumbnail_url')
          .filter((url: null | string) => !!url)
          .toArray(),
      );
    }
  }, [selectedMenu]);

  const productHeight = useMemo(() => {
    return isDesktop ? 190 : 145;
  }, [isDesktop]);

  const generateMenuScrollQuery = (categoryId: number, behavior: ScrollBehavior = 'smooth'): string => {
    return `?categoryId=${categoryId}&behavior=${behavior}`;
  };

  const context: MenuContextInterface = {
    menus,
    selectedMenu,
    productHeight,
    setSelectedMenu,
    categories,
    allCategories,
    compileProduct,
    findFreeProduct,
    applyOrderMethodPrices,
    getUpsellModifierGroup,
    generateMenuScrollQuery,
    loadingMenu: menusData === undefined,
  };

  return <MenuContext.Provider value={context}>{children}</MenuContext.Provider>;
}
